import { RootState } from "@store/index";
import { createSelector } from "reselect";
import {
  getHardwareModelFromPid,
  isSingleControllerDevice,
  reportsControllerState,
} from "@utils/productUtils";
import { HardwareModels } from "types/productTypes";
import { SCOPE_TYPE } from "@model";
import { selectReliabilityAutoRestartEnabled } from "@store/userPreferences/userPreferencesSlice";

export const selectProductState = (state: RootState) => state.product;

export const selectCurrentVersion = createSelector(
  [selectProductState],
  (state) => state.currentVersion,
);

export const selectIsEyeTrackingOn = createSelector(
  [selectProductState],
  (state) => state.isEyeTrackingOn,
);

export const selectProductId = createSelector([selectProductState], (state) =>
  state.pid == null ? null : String(state.pid),
);

export const selectIsSingleControllerDevice = createSelector([selectProductId], (pid) =>
  isSingleControllerDevice(pid),
);

export const selectReportsControllerState = createSelector([selectProductId], (pid) =>
  reportsControllerState(pid),
);

export const selectHardwareModel = createSelector([selectProductId], (pid) =>
  getHardwareModelFromPid(pid),
);

export const selectIsDpvrDevice = createSelector(
  [selectHardwareModel],
  (model) => model === HardwareModels.Dpvr,
);

export const selectProductAlias = createSelector([selectProductState], (state) => state.alias);

export const selectSelectedDeviceStatus = createSelector(
  [selectProductState],
  (state) => state.deviceStatus,
);

export const selectScopeList = createSelector([selectProductState], (state) => {
  return state.scopeList;
});

export const selectIsReliabilityAutoRestartAllowed = createSelector(
  [selectScopeList, selectReliabilityAutoRestartEnabled],
  (scopeList, enabledByUser) =>
    Boolean(scopeList?.includes(SCOPE_TYPE.reliabilityAutoRestart)) && enabledByUser,
);

export const selectScene = createSelector([selectProductState], (state) => state.scene);

export const selectIsVRInTestMode = createSelector(
  [selectScene],
  (scene) => scene && (scene?.toLowerCase() === "test" || scene?.toLowerCase() !== "menu"),
);

export const selectIsSceneMenu = createSelector(
  [selectProductState],
  (state) => state.scene === "Menu",
);

export const selectLastInteractionDate = createSelector(
  [selectProductState],
  (state) => state.lastInteractionDate,
);

export const selectLastTestInteractionDate = createSelector(
  [selectProductState],
  (state) => state.lastTestInteractionDate,
);

export const selectOnTestProgressPage = createSelector(
  [selectProductState],
  (state) => state.onTestProgressPage,
);

export const selectControllerConnectionStatus = createSelector(
  [selectProductState],
  (state) => state.controllers,
);

export const selectControllerBatteryState = createSelector(
  [selectProductState],
  (state) => state.controllerBattery,
);

export const selectDeviceStatus = createSelector(
  [selectProductState],
  (state) => state.deviceStatus,
);

export const selectProductStatus = createSelector([selectProductState], (state) => state.status);

export const selectVolume = createSelector([selectProductState], (state) => state.volume);

export const selectBattery = createSelector([selectProductState], (state) => state.battery);

export const selectOsVersion = createSelector([selectProductState], (state) => state.osVersion);

export const selectDevicePermissions = createSelector(
  [selectProductState],
  (state) => state.permissions,
);

export const selectAwaitingMenuSince = createSelector(
  [selectProductState],
  (state) => state.awaitingMenuSince,
);

// Stale reports from a finished run die within seconds (end animation is
// 2-5s); the window is generous while keeping the guard self-healing when
// the Menu scene is never observed (dropped push + missed poll).
export const STALE_TEST_SCENE_DISTRUST_WINDOW_MS = 30_000;

// Not a createSelector: the result depends on the current time, which
// memoization on state inputs would freeze.
export const selectIsAwaitingMenuSceneAfterCompletion = (state: RootState): boolean => {
  const since = state.product.awaitingMenuSince;
  return since != null && Date.now() - since < STALE_TEST_SCENE_DISTRUST_WINDOW_MS;
};
